import {
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  OutlinedInput,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { InsertDriveFile } from '@mui/icons-material'
import React, { useState } from 'react'
import { Helmet } from 'react-helmet'
import ReactPaginate from 'react-paginate'
import Moment from 'moment'
import ResponsiveAppBar from 'src/common/ResponsiveAppBar'
import { searchAllDocuments } from 'src/services/SearchService'
import { DocumentSearchRequest } from 'src/viewModels/DocumentSearchRequest'
import { DocumentSummaryResult } from 'src/viewModels/DocumentSummaryResult'
import { pagedList } from 'src/viewModels/pagedList'
import { formatDate } from 'src/utils/Formatter'
import { DocumentCategory } from 'src/utils/DocumentCategory'
import { DocumentType } from 'src/models/enums/DocumentType.enum'
import SortedTableCell, { sortedTableHeaderSx } from 'src/workflow/SortedTableCell'

const pageSize = 50

const DocumentSearch: React.FC = () => {
  const [gridData, setGridData] = useState<DocumentSummaryResult[]>(
    new pagedList().items as DocumentSummaryResult[]
  )
  const [searchFileName, setSearchFileName] = useState('')
  const [searchDescription, setSearchDescription] = useState('')
  const [searchDateStart, setSearchDateStart] = useState('')
  const [searchDateEnd, setSearchDateEnd] = useState('')
  const [searchCategory, setSearchCategory] = useState('')
  const [searchType, setSearchType] = useState('')
  const [searchProvider, setSearchProvider] = useState('')
  const [searchPatientFirstName, setSearchPatientFirstName] = useState('')
  const [searchPatientLastName, setSearchPatientLastName] = useState('')
  const [searchPatientDOB, setSearchPatientDOB] = useState('')
  const [searchFreeformAddress, setSearchFreeformAddress] = useState('')
  const [searchDateOfServiceStart, setSearchDateOfServiceStart] = useState('')
  const [searchDateOfServiceEnd, setSearchDateOfServiceEnd] = useState('')
  const [searchBodyPart, setSearchBodyPart] = useState('')
  const [searchTakeoverOfCareDateStart, setSearchTakeoverOfCareDateStart] = useState('')
  const [searchTakeoverOfCareDateEnd, setSearchTakeoverOfCareDateEnd] = useState('')
  const [searchMmiDateStart, setSearchMmiDateStart] = useState('')
  const [searchMmiDateEnd, setSearchMmiDateEnd] = useState('')
  const [searchEOC, setSearchEOC] = useState('')
  const [page, setPage] = useState(1)
  const [pageCount, setPageCount] = useState(0)
  const [firstRecordIndex, setFirstRecordIndex] = useState(0)
  const [lastRecordIndex, setLastRecordIndex] = useState(0)
  const [totalItems, setTotalItems] = useState(0)
  const [gridLoading, setGridLoading] = useState(false)
  const [sortColumn, setSortColumn] = useState('')
  const [sortOrder, setSortOrder] = useState('asc')

  const isGridDataEmpty = () => totalItems === 0

  const getDate = (value: string): Date | undefined => {
    return value ? new Date(value) : undefined
  }

  const setPaginationOffsetData = (targetPage: number, targetTotalItems: number) => {
    const firstIndex = (targetPage - 1) * pageSize + 1
    const lastIndex =
      targetPage * pageSize < targetTotalItems ? targetPage * pageSize : targetTotalItems

    setFirstRecordIndex(firstIndex)
    setLastRecordIndex(lastIndex)
  }

  const buildRequest = (targetPage: number) => {
    const requestData = new DocumentSearchRequest()
    requestData.page = targetPage
    requestData.pageSize = pageSize
    requestData.fileName = searchFileName
    requestData.description = searchDescription
    requestData.fileDateStart = getDate(searchDateStart)
    requestData.fileDateEnd = getDate(searchDateEnd)
    requestData.category = searchCategory
    requestData.type = searchType
    requestData.provider = searchProvider
    requestData.patientFirstName = searchPatientFirstName
    requestData.patientLastName = searchPatientLastName
    requestData.patientDOB = getDate(searchPatientDOB)
    requestData.freeformAddress = searchFreeformAddress
    requestData.dateOfServiceStart = getDate(searchDateOfServiceStart)
    requestData.dateOfServiceEnd = getDate(searchDateOfServiceEnd)
    requestData.bodyPart = searchBodyPart
    requestData.takeoverOfCareDateStart = getDate(searchTakeoverOfCareDateStart)
    requestData.takeoverOfCareDateEnd = getDate(searchTakeoverOfCareDateEnd)
    requestData.mmiDateStart = getDate(searchMmiDateStart)
    requestData.mmiDateEnd = getDate(searchMmiDateEnd)
    requestData.episodeOfCareNumberOrLegacyId = searchEOC
    requestData.sorting = sortColumn
      ? { column: sortColumn, order: sortOrder }
      : undefined

    return requestData
  }

  const getDocumentData = async (targetPage = page) => {
    setGridLoading(true)

    try {
      const response = await searchAllDocuments(buildRequest(targetPage))
      const responseTotalItems = response.totalItems ?? 0
      const responseTotalPages = response.totalPages ?? 0

      setGridData(response.items as DocumentSummaryResult[])
      setPageCount(responseTotalPages)
      setTotalItems(responseTotalItems)

      if (responseTotalItems > 0) {
        setPaginationOffsetData(targetPage, responseTotalItems)
      } else {
        setFirstRecordIndex(0)
        setLastRecordIndex(0)
      }
    } finally {
      setGridLoading(false)
    }
  }

  const search = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setPage(1)
    getDocumentData(1)
  }

  const clearSearch = () => {
    setSearchFileName('')
    setSearchDescription('')
    setSearchDateStart('')
    setSearchDateEnd('')
    setSearchCategory('')
    setSearchType('')
    setSearchProvider('')
    setSearchPatientFirstName('')
    setSearchPatientLastName('')
    setSearchPatientDOB('')
    setSearchFreeformAddress('')
    setSearchDateOfServiceStart('')
    setSearchDateOfServiceEnd('')
    setSearchBodyPart('')
    setSearchTakeoverOfCareDateStart('')
    setSearchTakeoverOfCareDateEnd('')
    setSearchMmiDateStart('')
    setSearchMmiDateEnd('')
    setSearchEOC('')
    setPage(1)
    setPageCount(0)
    setFirstRecordIndex(0)
    setLastRecordIndex(0)
    setTotalItems(0)
    setGridData([])
  }

  const handlePageChange = (event: any) => {
    const targetPage = event.selected + 1
    setPage(targetPage)
    getDocumentData(targetPage)
  }

  const openDocumentItem = (associatedItemId: string, url: string) => {
    if (associatedItemId) {
      window.open(`/workflow/document/${associatedItemId}/default-inbox`, '_blank')
    } else if (url) {
      window.open(url, '_blank')
    }
  }

  const sort = (columnName: string) => {
    const nextSortOrder = sortOrder === 'asc' ? 'desc' : 'asc'
    setSortColumn(columnName)
    setSortOrder(nextSortOrder)
    setPage(1)
    setGridLoading(true)
    searchAllDocuments({
      ...buildRequest(1),
      sorting: { column: columnName, order: nextSortOrder },
    })
      .then((response) => {
        setGridData(response.items as DocumentSummaryResult[])
        setPageCount(response.totalPages ?? 0)
        setTotalItems(response.totalItems ?? 0)
        if ((response.totalItems ?? 0) > 0) {
          setPaginationOffsetData(1, response.totalItems ?? 0)
        } else {
          setFirstRecordIndex(0)
          setLastRecordIndex(0)
        }
      })
      .finally(() => setGridLoading(false))
  }

  const renderSortedHeader = (
    columnName: string,
    propertyName: string,
    align?: 'left' | 'center' | 'right'
  ) => (
    <SortedTableCell
      columnName={columnName}
      propertyName={propertyName}
      align={align}
      order={sortOrder}
      orderBy={sortColumn}
      onClick={sort}
      reserveSortIconSpace
    />
  )

  const paginationInfo = () => {
    if (!isGridDataEmpty()) {
      return (
        <div className="pagination">
          <Typography variant="caption" gutterBottom>
            Showing {firstRecordIndex} to {lastRecordIndex} of {totalItems} entries
          </Typography>
        </div>
      )
    }

    return <></>
  }

  const renderDatePicker = (
    label: string,
    value: string,
    onChange: (value: string) => void
  ) => (
    <DatePicker
      value={value ? Moment(value) : null}
      onChange={(date: any) => onChange(date ? Moment(date).format('YYYY-MM-DD') : '')}
      label={label}
      slotProps={{
        textField: {
          fullWidth: true,
          variant: 'outlined',
          InputLabelProps: { shrink: true },
        },
        openPickerIcon: { fontSize: 'small' },
      }}
    />
  )

  const renderDateRange = (
    startLabel: string,
    startValue: string,
    setStartValue: (value: string) => void,
    endLabel: string,
    endValue: string,
    setEndValue: (value: string) => void
  ) => (
    <Grid item container xs={12} md={6} lg={3} spacing={0} alignItems="center">
      <Grid item xs={5.5}>
        {renderDatePicker(startLabel, startValue, setStartValue)}
      </Grid>
      <Grid item xs={1} style={{ textAlign: 'center' }}>
        <strong> - </strong>
      </Grid>
      <Grid item xs={5.5}>
        {renderDatePicker(endLabel, endValue, setEndValue)}
      </Grid>
    </Grid>
  )

  const renderGridData = () => {
    if (isGridDataEmpty()) {
      return (
        <TableRow key={1}>
          <TableCell colSpan={10} align="center">
            No records found
          </TableCell>
        </TableRow>
      )
    }

    return gridData.map((doc, index) => (
      <TableRow key={index} className="gridPadding">
        <TableCell>
          <Button
            variant="contained"
            color="primary"
            onClick={() => openDocumentItem(doc.associatedItemId, doc.fileUrl)}
          >
            Open
          </Button>
        </TableCell>
        <TableCell>{doc.fileName}</TableCell>
        <TableCell>{doc.description}</TableCell>
        <TableCell>{doc.categoryDescription}</TableCell>
        <TableCell>{doc.typeDescription}</TableCell>
        <TableCell>{doc.episodeOfCareNumberOrLegacyId}</TableCell>
        <TableCell style={{ whiteSpace: 'nowrap' }}>{doc.createdBy}</TableCell>
        <TableCell align="right" style={{ whiteSpace: 'nowrap' }}>
          {doc.fileDate ? formatDate(doc.fileDate.toString()) : ''}
        </TableCell>
      </TableRow>
    ))
  }

  return (
    <>
      <Helmet>
        <title>Document Search</title>
      </Helmet>
      <ResponsiveAppBar title="Document Search" pageIcon={<InsertDriveFile />} />
      {gridLoading && (
        <div
          style={{
            position: 'fixed',
            display: 'flex',
            width: '100%',
            height: '100%',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            cursor: 'pointer',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: '2',
          }}
        >
          <CircularProgress size={100} />
        </div>
      )}
      <Paper
        style={{ margin: '20px', padding: '20px', paddingBottom: '20px' }}
        elevation={3}
      >
        <form onSubmit={search}>
          <Grid container style={{ margin: '10px' }}>
            <Typography
              color="primary"
              component="h6"
              variant="h6"
              gutterBottom
              style={{ paddingBottom: '10px' }}
            >
              SEARCH CRITERIA
            </Typography>
            <Grid container direction="row" spacing={2}>
              {renderDateRange(
                'File Date Start',
                searchDateStart,
                setSearchDateStart,
                'File Date End',
                searchDateEnd,
                setSearchDateEnd
              )}
              <Grid item xs={12} md={6} lg={3}>
                <FormControl fullWidth>
                  <TextField
                    value={searchFileName}
                    onChange={(event) => setSearchFileName(event.target.value)}
                    label="File Name"
                    name="fileName"
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6} lg={2}>
                <FormControl style={{ width: '100%' }} variant="outlined">
                  <InputLabel shrink={true} variant="outlined">
                    Category
                  </InputLabel>
                  <Select
                    value={searchCategory}
                    variant="outlined"
                    input={<OutlinedInput notched label="Category" />}
                    native={true}
                    fullWidth
                    onChange={(event: SelectChangeEvent<string>) =>
                      setSearchCategory(event.target.value)
                    }
                  >
                    <option value={''}>Select a category</option>
                    <option value={DocumentCategory.Authorization}>Authorization</option>
                    <option value={DocumentCategory.Billing}>Billing</option>
                    <option value={DocumentCategory.Contracts}>Contracts</option>
                    <option value={DocumentCategory.Medical}>Medical</option>
                    <option value={DocumentCategory.Misc}>Miscellaneous</option>
                    <option value={DocumentCategory.PT}>PT</option>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <FormControl style={{ width: '100%' }} variant="outlined">
                  <InputLabel shrink={true} variant="outlined">
                    Type
                  </InputLabel>
                  <Select
                    value={searchType}
                    variant="outlined"
                    input={<OutlinedInput notched label="Type" />}
                    native={true}
                    fullWidth
                    onChange={(event: SelectChangeEvent<string>) =>
                      setSearchType(event.target.value)
                    }
                  >
                    <option value={''}>Select a type</option>
                    <option value={DocumentType.PatientSurgicalContract}>
                      Agreement for Surgical Services
                    </option>
                    <option value={DocumentType.PatientInjectionContract}>
                      Agreement for Surgical Injection Services
                    </option>
                    <option value={DocumentType.BillOut1500}>Bill-Out 1500</option>
                    <option value={DocumentType.ClaimDenialLetter}>
                      Claim Denial Letter
                    </option>
                    <option value={DocumentType.CMS1500}>CMS 1500</option>
                    <option value={DocumentType.Combined1500}>Combined 1500</option>
                    <option value={DocumentType.DME}>DME</option>
                    <option value={DocumentType.ExplanationOfBenefits}>
                      Explanation of Benefits
                    </option>
                    <option value={DocumentType.FCE}>FCE</option>
                    <option value={DocumentType.WorkHardening}>Work Hardening</option>
                    <option value={DocumentType.InHome}>In-Home</option>
                    <option value={DocumentType.InitialEval}>Initial Eval</option>
                    <option value={DocumentType.InpatientRehab}>Inpatient Rehab</option>
                    <option value={DocumentType.InvoiceInitial}>Initial Invoice</option>
                    <option value={DocumentType.PatientDemographics}>
                      Patient Demographics
                    </option>
                    <option value={DocumentType.InvoiceResend}>Resend Invoice</option>
                    <option value={DocumentType.OccupationalTherapy}>
                      Occupational Therapy
                    </option>
                    <option value={DocumentType.PhysicalTherapy}>Physical Therapy</option>
                    <option value={DocumentType.SurgeryFacility}>
                      Surgery / Facility
                    </option>
                    <option value={DocumentType.TakeoverOfCare}>Takeover of Care</option>
                    <option value={DocumentType.Upload}>User Uploaded</option>
                    <option value={DocumentType.WorkConditioning}>
                      Work Conditioning
                    </option>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6} lg={2}>
                <FormControl fullWidth>
                  <TextField
                    value={searchPatientFirstName}
                    onChange={(event) => setSearchPatientFirstName(event.target.value)}
                    label="Patient First Name"
                    name="patientFirstName"
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6} lg={2}>
                <FormControl fullWidth>
                  <TextField
                    value={searchPatientLastName}
                    onChange={(event) => setSearchPatientLastName(event.target.value)}
                    label="Patient Last Name"
                    name="patientLastName"
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6} lg={2}>
                {renderDatePicker('Patient DOB', searchPatientDOB, setSearchPatientDOB)}
              </Grid>
              <Grid item xs={12} md={6} lg={2}>
                <FormControl fullWidth>
                  <TextField
                    value={searchEOC}
                    onChange={(event) => setSearchEOC(event.target.value)}
                    label="EOC/Legacy"
                    name="eoc"
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6} lg={2}>
                <FormControl fullWidth>
                  <TextField
                    value={searchBodyPart}
                    onChange={(event) => setSearchBodyPart(event.target.value)}
                    label="Body Part"
                    name="bodyPart"
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                  />
                </FormControl>
              </Grid>
              {renderDateRange(
                'DOS Start Date',
                searchDateOfServiceStart,
                setSearchDateOfServiceStart,
                'DOS End Date',
                searchDateOfServiceEnd,
                setSearchDateOfServiceEnd
              )}
              {renderDateRange(
                'TOC Start Date',
                searchTakeoverOfCareDateStart,
                setSearchTakeoverOfCareDateStart,
                'TOC End Date',
                searchTakeoverOfCareDateEnd,
                setSearchTakeoverOfCareDateEnd
              )}
              {renderDateRange(
                'MMI Start Date',
                searchMmiDateStart,
                setSearchMmiDateStart,
                'MMI End Date',
                searchMmiDateEnd,
                setSearchMmiDateEnd
              )}
              <Grid item xs={12} md={12} lg={4}>
                <FormControl fullWidth>
                  <TextField
                    value={searchProvider}
                    onChange={(event) => setSearchProvider(event.target.value)}
                    label="Provider"
                    name="provider"
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} md={12} lg={4}>
                <FormControl fullWidth>
                  <TextField
                    value={searchFreeformAddress}
                    onChange={(event) => setSearchFreeformAddress(event.target.value)}
                    label="Freeform Address"
                    name="freeformAddress"
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                  />
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
          <Grid
            container
            direction="row"
            spacing={2}
            alignItems="center"
            style={{
              marginLeft: '1px',
              paddingBottom: '10px',
              paddingTop: '10px',
            }}
          >
            <Grid item style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Button type="submit" color="primary" size="medium" variant="contained">
                Search
              </Button>
              <Button
                color="secondary"
                onClick={clearSearch}
                size="medium"
                variant="contained"
              >
                Clear Search
              </Button>
            </Grid>
          </Grid>
        </form>
        <Grid container>
          <Grid item xs={12} style={{ paddingTop: '16px' }}>
            <Table aria-label="simple table" sx={sortedTableHeaderSx}>
              <TableHead>
                <TableRow className="gridPadding">
                  <TableCell></TableCell>
                  {renderSortedHeader('File Name', 'FileName')}
                  {renderSortedHeader('Description', 'Description')}
                  {renderSortedHeader('Category', 'CategoryDescription')}
                  {renderSortedHeader('Type', 'TypeDescription')}
                  {renderSortedHeader('EOC/Legacy', 'EpisodeOfCareNumberOrLegacyId')}
                  {renderSortedHeader('User', 'CreatedBy')}
                  {renderSortedHeader('Date', 'FileDate', 'right')}
                </TableRow>
              </TableHead>
              <TableBody style={{ width: '100%' }}>{renderGridData()}</TableBody>
            </Table>
            <div className="pagination-row">
              {paginationInfo()}
              <ReactPaginate
                previousLabel={'<'}
                nextLabel={'>'}
                onPageChange={handlePageChange}
                pageCount={pageCount}
                containerClassName={'pagination'}
                activeClassName={'active'}
                forcePage={page - 1}
                //@ts-ignore
                renderOnZeroPageCount={null}
              />
            </div>
          </Grid>
        </Grid>
      </Paper>
    </>
  )
}

export default DocumentSearch
